import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Search, X, Plus } from 'lucide-react';
import { fetchGlobalSearchResults } from '../../../redux/searchBar/searchActions';
import debounce from 'lodash.debounce';
import StockLogo from '../../../components/StockLogo/StockLogoRender';

const StockSelector = ({
  selectedStocks = [],
  onStocksChange,
  placeholder,
  maxStocks = 10,
  disabled = false
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('groups.stockSelector.defaultPlaceholder');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef(null);
  const dispatch = useDispatch();

  // Get stocks + status from Redux
  const { stocks: searchResults = [], status } = useSelector((state) => state.search || {});
  const isSearching = status === 'loading';

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      if (searchQuery.trim().length > 0) {
        dispatch(fetchGlobalSearchResults(searchQuery));
      }
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter out already selected stocks
  const filteredResults = searchResults.filter(
    stock => !selectedStocks.some(selected => 
      (selected.symbol === (stock.Symbol || stock.symbol)) ||
      (selected.id === stock.id)
    )
  );

  const handleStockSelect = (stock) => {
    if (selectedStocks.length >= maxStocks) return;
    
    // Normalize the stock data structure
    const normalizedStock = {
      id: stock.id || stock.ID, // Ensure we have an ID
      symbol: stock.symbol || stock.Symbol, 
      name: stock.name || stock.Name,
      logo_url: stock.logo_url || stock.LogoURL || null,
    };
    
    const newStocks = [...selectedStocks, normalizedStock];
    onStocksChange?.(newStocks);
    setQuery('');
    setIsOpen(false);
  };

  const handleStockRemove = (stockToRemove) => {
    const newStocks = selectedStocks.filter(stock => 
      stock.symbol !== stockToRemove.symbol && stock.id !== stockToRemove.id
    );
    onStocksChange?.(newStocks);
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div ref={searchRef} className="relative">
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={resolvedPlaceholder}
            disabled={disabled || selectedStocks.length >= maxStocks}
            className="w-full bg-white dark:bg-gray-900/50 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {selectedStocks.length >= maxStocks && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-orange-700 dark:text-orange-400 bg-orange-400/10 px-2 py-1 rounded">
              {t('groups.stockSelector.maxLabel', { max: maxStocks })}
            </div>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
            {query.trim().length === 0 ? (
              <div className="p-4 text-center text-gray-600 dark:text-gray-400 text-sm">
                {t('groups.stockSelector.startTypingSearch')}
              </div>
            ) : isSearching ? (
              <div className="p-4 text-center">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <span className="text-gray-600 dark:text-gray-400 text-sm">{t('groups.stockSelector.searchingStocks')}</span>
              </div>
            ) : filteredResults.length > 0 ? (
              <div className="p-2">
                {filteredResults.map((stock, index) => (
                  <button
                    key={`${stock.Symbol || stock.symbol}-${stock.id || index}`}
                    onClick={() => handleStockSelect(stock)}
                    disabled={selectedStocks.length >= maxStocks}
                    className="w-full p-3 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors duration-200 text-left flex items-center justify-between group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center space-x-3">
                      <StockLogo
                        symbol={stock.Symbol || stock.symbol}
                        name={stock.Name || stock.name}
                        size="w-8 h-8"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{stock.Symbol || stock.symbol}</div>
                        <div className="text-gray-600 dark:text-gray-400 text-sm truncate max-w-[200px]">
                          {stock.Name || stock.name}
                        </div>
                      </div>
                    </div>
                    <Plus size={16} className="text-gray-400 dark:text-gray-400 group-hover:text-blue-400 group-disabled:text-gray-400 dark:group-disabled:text-gray-600 transition-colors duration-200" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-600 dark:text-gray-400">
                {t('groups.stockSelector.noStocksFoundFor', { query })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Stocks Display */}
      {selectedStocks.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {t('groups.stockSelector.selectedStocksLabel', { count: selectedStocks.length, max: maxStocks })}
            </label>
            <button
              onClick={() => onStocksChange?.([])}
              className="text-xs text-red-400 hover:text-red-300 transition-colors duration-200 hover:underline"
              disabled={disabled}
            >
              {t('groups.stockSelector.clearAll')}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedStocks.map((stock) => (
              <div
                key={`selected-${stock.symbol}-${stock.id}`}
                className="bg-gradient-to-r from-violet-600/20 to-indigo-600/20 border border-indigo-500/30 rounded-full px-3 py-2 text-sm flex items-center space-x-2 group hover:from-violet-600/30 hover:to-indigo-600/30 transition-all duration-200"
              >
                <StockLogo
                  symbol={stock.symbol}
                  name={stock.name}
                  size="w-5 h-5"
                />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-indigo-300">{stock.symbol}</span>
                  {stock.name && (
                    <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[80px]">
                      {stock.name}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleStockRemove(stock)}
                  disabled={disabled}
                  className="p-1 hover:bg-red-500/20 rounded-full transition-colors duration-200 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 disabled:opacity-0 disabled:cursor-not-allowed"
                  title={t('groups.stockSelector.removeStock')}
                >
                  <X size={12} className="text-red-400 hover:text-red-300" />
                </button>
              </div>
            ))}
          </div>
          {selectedStocks.length >= maxStocks && (
            <p className="text-xs text-orange-700 dark:text-orange-400 bg-orange-400/10 px-3 py-2 rounded-lg">
              {t('groups.stockSelector.maxStocksReached', { max: maxStocks })}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StockSelector;